import moment from 'moment';
import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { AvailabilitySlot } from 'shared-types/Common';
import type { TimeslotOption, DayAvailability } from 'voyager';

type TimeslotAvailabilityParams = {
  rawTimeslots: AvailabilitySlot[] | undefined;
  selectedDateString: string;
  weekStartDate: string;
  form: UseFormReturn<any>;
  onWeekNavigate: (date: string) => void;
};

function groupSlotsByDate(slots: AvailabilitySlot[]): Record<string, AvailabilitySlot[]> {
  const grouped: Record<string, AvailabilitySlot[]> = {};
  slots.forEach((slot) => {
    const date = moment(slot.startDateTime).format('YYYY-MM-DD');
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(slot);
  });
  return grouped;
}

export function useTimeslotAvailability({
  rawTimeslots,
  selectedDateString,
  weekStartDate,
  form,
  onWeekNavigate,
}: TimeslotAvailabilityParams) {
  const slotsByDate = useMemo(() => {
    if (!rawTimeslots || !Array.isArray(rawTimeslots)) return {};
    return groupSlotsByDate(rawTimeslots);
  }, [rawTimeslots]);

  const availability: DayAvailability[] = useMemo(() => {
    const days: DayAvailability[] = [];
    for (let i = 0; i < 7; i++) {
      const date = moment(weekStartDate).add(i, 'days').format('YYYY-MM-DD');
      days.push({
        date,
        available: !!(slotsByDate[date] && slotsByDate[date].length > 0),
      });
    }
    return days;
  }, [slotsByDate, weekStartDate]);

  const timeslotOptions: TimeslotOption[] = useMemo(() => {
    const daySlots = slotsByDate[selectedDateString];
    if (!daySlots || daySlots.length === 0) return [];
    return daySlots.map((slot) => {
      const start = moment(slot.startDateTime).format('HH:mm');
      const end = moment(slot.endDateTime).format('HH:mm');
      return {
        value: start,
        label: `${start} - ${end}`,
      };
    });
  }, [slotsByDate, selectedDateString]);

  const handleDateSelect = (date: string) => {
    form.setValue('date', date);
    form.setValue('arrivalTime', undefined as any);
  };

  const handleWeekNavigate = (date: string) => {
    onWeekNavigate(date);
  };

  return {
    slotsByDate,
    availability,
    timeslotOptions,
    handleDateSelect,
    handleWeekNavigate,
  };
}
